import { graphql, Link, useStaticQuery } from 'gatsby';
import Cookies from 'js-cookie';
import { MapPin } from 'lucide-react';
import React from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/utils';
import { getLocalizedLink, isExternalSlug } from '@/utils/getLocalizedLink';

import { MainMenuPreOrderLink } from './MainMenuPreOrderLink';
import { PanelEnum, usePanelStore } from './Panels';

export const MainMenu: React.FC = () => {
  const { campaign, products } = useStaticQuery<Queries.MainMenuQueryQuery>(
    graphql`
      query MainMenuQuery {
        campaign: contentfulCampaign(fields: { activeCampaign: { eq: true } }) {
          navigation {
            primaryLinks {
              ... on ContentfulLink {
                ...Link
              }
            }
            secondaryLinks {
              ... on ContentfulLink {
                ...Link
              }
            }
            tertiaryLinks {
              ... on ContentfulLink {
                ...Link
              }
            }
          }
        }

        products: allContentfulPageCategory(
          filter: {
            title: {
              regex: "/^(?!(.*DO NOT DELETE.*|Hem X|Gift cards|Parts)).*$/"
            }
            # totalSkus: { gt: 0 }
          }
        ) {
          nodes {
            title
            slug
          }
        }
      }
    `,
  );

  const { primaryLinks, secondaryLinks, tertiaryLinks } = campaign.navigation;

  const { setCurrentlyOpenPanel } = usePanelStore();
  const closeMainMenuPanel = () => {
    setCurrentlyOpenPanel(PanelEnum.None);
  };

  return (
    <nav className="mb-8 grid grid-rows-[auto,1fr,auto,auto] md:pr-4">
      <ul className="mb-8 divide-y">
        <Accordion type="multiple">
          <AccordionItem value="Products" className="border-b-0">
            <AccordionTrigger className="text-black border-b-0 py-2 pr-0.5 text-left text-xl font-bold">
              Products
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              <ul className="divide-y">
                {Array.from(products.nodes)
                  .map((link) => ({
                    ...link,
                    slug: getLocalizedLink(link.slug),
                  }))
                  .sort((a, b) => {
                    const order = [
                      '/furniture/chairs-and-stools',
                      '/furniture/sofas-and-poufs',
                      '/furniture/couches-and-poufs',
                      '/furniture/tables-and-side-tables',
                      '/furniture/shelving-and-storage',
                      '/furniture/outdoor-furniture',
                      '/lighting',
                      '/rugs',
                      '/accessories/tableware',
                      '/accessories/throws-and-cushions',
                      '/accessories/home-and-office-decor',
                    ];
                    const aIndex =
                      order.findIndex((o) => a.slug.includes(o)) !== -1
                        ? order.findIndex((o) => a.slug.includes(o))
                        : order.length;
                    const bIndex =
                      order.findIndex((o) => b.slug.includes(o)) !== -1
                        ? order.findIndex((o) => b.slug.includes(o))
                        : order.length;
                    return aIndex - bIndex;
                  })
                  .map((link) => {
                    return (
                      <li key={link.slug}>
                        <Link
                          to={getLocalizedLink(link.slug)}
                          onClick={closeMainMenuPanel}
                          className={cn(
                            'text-black block py-2 text-lg font-bold',
                            {
                              'text-hem-x hover:text-hem-x': getLocalizedLink(
                                link.slug,
                              ).startsWith('/hem-x'),
                            },
                          )}
                        >
                          {link.title}
                        </Link>
                      </li>
                    );
                  })}
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {primaryLinks.map((link) => {
          if (link.__typename !== 'ContentfulLink') return null;

          return (
            <li key={link.contentful_id}>
              {link.entry?.slug ? (
                <Link
                  to={getLocalizedLink(link.entry.slug)}
                  onClick={closeMainMenuPanel}
                  className={cn('text-black block py-2 text-xl font-bold', {
                    'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                    'text-blue hover:text-blue': link.color === 'Blue',
                    'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                  })}
                >
                  {link.title}
                </Link>
              ) : isExternalSlug(link.slug) ? (
                <a
                  href={link.slug}
                  className={cn('text-black block py-2 text-xl font-bold', {
                    'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                    'text-blue hover:text-blue': link.color === 'Blue',
                    'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                  })}
                >
                  {link.title}
                </a>
              ) : (
                <Link
                  to={getLocalizedLink(link.slug)}
                  onClick={closeMainMenuPanel}
                  className={cn('text-black block py-2 text-xl font-bold', {
                    'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                    'text-blue hover:text-blue': link.color === 'Blue',
                    'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                  })}
                >
                  {link.title}
                </Link>
              )}
            </li>
          );
        })}
      </ul>

      <ul className="mb-8">
        <MainMenuPreOrderLink onClick={closeMainMenuPanel} />

        {secondaryLinks.map((link) => (
          <li key={link.slug}>
            {link.entry?.slug ? (
              <Link
                to={getLocalizedLink(link.entry.slug)}
                onClick={closeMainMenuPanel}
                className={cn('text-black block py-1 text-lg font-bold', {
                  'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                  'text-blue hover:text-blue': link.color === 'Blue',
                  'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                })}
              >
                {link.title}
              </Link>
            ) : isExternalSlug(link.slug) ? (
              <a
                href={link.slug}
                className={cn('text-black block py-1 text-lg font-bold', {
                  'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                  'text-blue hover:text-blue': link.color === 'Blue',
                  'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                })}
              >
                {link.title}
              </a>
            ) : (
              <Link
                to={getLocalizedLink(link.slug)}
                onClick={closeMainMenuPanel}
                className={cn('text-black block py-1 text-lg font-bold', {
                  'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                  'text-blue hover:text-blue': link.color === 'Blue',
                  'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                })}
              >
                {link.title}
              </Link>
            )}
          </li>
        ))}
      </ul>

      <ul className="mb-8 grid grid-cols-2 gap-2">
        {tertiaryLinks.map((link) => (
          <li key={link.slug}>
            {link.entry?.slug ? (
              <Link
                to={getLocalizedLink(link.entry.slug)}
                onClick={closeMainMenuPanel}
                className={cn('text-black text-base', {
                  'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                  'text-blue hover:text-blue': link.color === 'Blue',
                  'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                })}
              >
                {link.title}
              </Link>
            ) : isExternalSlug(link.slug) ? (
              <a
                href={link.slug}
                className={cn('text-black text-base', {
                  'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                  'text-blue hover:text-blue': link.color === 'Blue',
                  'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                })}
              >
                {link.title}
              </a>
            ) : (
              <Link
                to={getLocalizedLink(link.slug)}
                onClick={closeMainMenuPanel}
                className={cn('text-black text-base', {
                  'text-red-gloss hover:text-red-gloss': link.color === 'Red',
                  'text-blue hover:text-blue': link.color === 'Blue',
                  'text-hem-x hover:text-hem-x': link.color === 'Hem X',
                })}
              >
                {link.title}
              </Link>
            )}
          </li>
        ))}
      </ul>

      <button
        className="hover:text-black flex items-center gap-2 border-t pt-4 text-base text-black-600 hover:underline"
        onClick={() => {
          Cookies.remove('hem-region-default');
          window.location.href = window.location.origin;
        }}
      >
        <MapPin size={20} />
        Change location
      </button>
    </nav>
  );
};

export default MainMenu;
